import { useRef } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select, { components, StylesConfig } from "react-select";
import styled from "styled-components";

import { appTheme } from "../../../theme/Theme";
import AppText, { FormLabel } from "../AppText/AppText";

export interface DefaultOptionType {
	id: string;
	name: string;
}

const DropdownIndicator = (props: any) => {
	const isSimpleLayout = props?.selectProps?.isSimpleLayout;

	return (
		<components.DropdownIndicator {...props}>
			<img
				style={{ marginRight: "0.5rem" }}
				width="10px"
				src={!isSimpleLayout ? "/svg/drop-down-indicator.svg" : "/svg/sorting-arrow-down.svg"}
				alt="down-arrow"
			/>
		</components.DropdownIndicator>
	);
};

interface IDropdownProps<O, V> {
	options?: O[];
	name?: string;
	onChange?: (val: V) => any;
	onMenuOpen?: () => any;
	onMenuClose?: () => any;
	height?: string;
	value?: any;
	label?: string;
	disabled?: boolean;
	errors?: any;
	inputRef?: any;
	width?: string;
	placeholder?: string;
	margin?: string;
	padding?: string;
	getOptionLabel?: (option: O) => string;
	getValue: (option: O) => V;
	description?: string;
	mandatory?: boolean;
	isSimpleLayout?: boolean;
	defaultValue?: O[];
	/**
	 * Optional tooltip ℹ️
	 */
	tooltip?: string | React.ReactNode;
	/**
	 * Optional knowledge base tag to open knowledge base sidebar
	 */
	knowledgeBaseTag?: string;
	/**
	 * Optional custom tooltip text for knowledge base trigger
	 */
	knowledgeBaseTooltip?: string;
	/**
	 * Optional knowledge base tag that only shows on hover (reduces visual clutter)
	 */
	showKnowledgeBaseForTagOnHover?: string;
}

interface IDropdownPropsStyles {
	width?: string;
	margin?: string;
	padding?: string;
}

function Dropdown<O extends Record<string, any>, V = string>({
	options,
	name,
	onChange,
	onMenuOpen,
	onMenuClose,
	height,
	value,
	label,
	disabled,
	errors,
	inputRef,
	width,
	placeholder,
	getOptionLabel,
	getValue,
	description,
	mandatory,
	isSimpleLayout,
	margin,
	padding,
	defaultValue,
	tooltip,
	knowledgeBaseTag,
	knowledgeBaseTooltip,
	showKnowledgeBaseForTagOnHover,
	...props
}: IDropdownProps<O, V>) {
	// @ts-ignore: Cant tame ReactSelect options
	const customStyles: StylesConfig<O, false> = {
		control: (provided: any) => ({
			...provided,
			color: "black",
			borderRadius: "2px",
			boxShadow: "none",
			border: "none",
			fontSize: appTheme.fontSizes.FONT_SIZE_M,
			backgroundColor: !isSimpleLayout ? "#fff" : "transparent",
			minHeight: "2.3125rem",
		}),

		container: (provided: any, state: any) => ({
			...provided,
			borderColor: state.isFocused && "#ccc",
			width: "100%",
			fontSize: appTheme.fontSizes.FONT_SIZE_M,
			border: !isSimpleLayout ? "1px solid #dbdbdb" : "none",
		}),

		indicatorSeparator: (provided: any) => ({
			...provided,
			display: "none",
		}),

		placeholder: (provided: any) => ({
			...provided,
			color: appTheme.colors.COLOR_GRAY_TEXT,
			fontSize: "0.9375rem",
		}),

		option: (provided: any, state: any) => ({
			...provided,
			backgroundColor: state.isSelected ? "#ccc" : "#fff",
			color: "black",
		}),

		menuList: (provided: any) => ({
			...provided,
			height: height ? height : "auto",
		}),
		menuPortal: (base: any) => ({
			...base,
			zIndex: 9999, // Make sure it's above modal
		}),
		menu: (provided: any) => ({
			...provided,
			zIndex: 9999,
		}),

		valueContainer: (provided: any) => ({
			...provided,
			padding: !isSimpleLayout ? "0.2rem 0.8rem" : "0.2rem 0.8rem 0.2rem 0",
		}),

		singleValue: (provided: any) => ({
			...provided,
			fontSize: appTheme.fontSizes.FONT_SIZE_M,
		}),
	};

	const focusElementRef = useRef<any>();
	const { t } = useTranslation();

	const handleCustomOnChange = (option: O | null) => {
		if (!onChange || !option) {
			if (!option && onChange) {
				onChange(null as unknown as V);
			} else {
				return;
			}
		} else {
			onChange(getValue(option));
		}
	};

	const theValue = getValue ? (options || []).find((o) => getValue(o) === value) : value;

	return (
		<DropdownContainer width={width} margin={margin} padding={padding}>
			<div>
				{label && (
					<FormLabel
						tooltip={tooltip}
						knowledgeBaseTag={knowledgeBaseTag}
						knowledgeBaseTooltip={knowledgeBaseTooltip}
						showKnowledgeBaseForTagOnHover={showKnowledgeBaseForTagOnHover}
					>
						{label && t(label as any)} {mandatory ? " *" : ""}
					</FormLabel>
				)}
				<Select<O>
					{...props}
					innerRef={inputRef}
					name={name}
					placeholder={placeholder ? t(placeholder as any) : t("SELECT_PLACEHOLDER")}
					components={{ DropdownIndicator }}
					styles={customStyles}
					options={options}
					onChange={handleCustomOnChange}
					getOptionLabel={getOptionLabel}
					onMenuOpen={onMenuOpen}
					onMenuClose={onMenuClose}
					//@ts-ignore
					getOptionValue={(o) => (getValue ? getValue(o) : o.id || o.key || o.type || o)}
					value={theValue}
					isDisabled={disabled}
					onFocus={() => focusElementRef.current?.focus()}
					isSimpleLayout={isSimpleLayout || false}
					isClearable
					defaultValue={defaultValue}
				/>
			</div>
			{description && (
				<AppText margin="0.3rem 0 0 0.15rem" size="xs" color="grayText">
					{t(description as any)}
				</AppText>
			)}
			{errors && (
				<AppText margin="0.3rem 0 0 0.15rem" size="xs" color="danger">
					{errors && t((errors?.message || errors) as any)}
				</AppText>
			)}
		</DropdownContainer>
	);
}

export default Dropdown;

interface IDropdownPropsController<O, V> extends IDropdownProps<O, V> {
	control: Control<any, any>;
	name: string;
	rules?: object;
}

export function DropdownController<O extends Record<string, any>, V = string>({
	control,
	rules,
	name,
	onChange: oOnChange,
	...rest
}: IDropdownPropsController<O, V>) {
	return (
		<Controller
			name={name}
			rules={rules}
			control={control}
			render={({ field: { ref, ...fieldWithoutRef } }) => (
				<Dropdown<O, V> {...rest} {...fieldWithoutRef} />
			)}
		/>
	);
}

const DropdownContainer = styled.div<IDropdownPropsStyles>`
	width: ${({ width }) => width || "100%"};
	padding: ${({ padding }) => padding || "0"};
	margin: ${({ margin }) => margin || "0 0 1rem 0"};
	font-size: 15px;

	:disabled {
		background: #dddddd;
		cursor: not-allowed;
	}
`;
